/**
 * OrgContext — provides the current user's accessible orgs and the active
 * org throughout the SPA. Auth is cookie-based (vp-session); no Supabase.
 *
 * The worker scopes every authenticated query by the session's active_org_id,
 * so we don't need to inject an X-Org-Id header. Switching orgs is a single
 * POST to /api/me/active-org which updates the session row.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';

interface Org {
  id: string;
  name: string;
  slug: string;
  role: string;
  is_demo: boolean;
  mfa_required: boolean;
  server_count: number;
  logo_url: string | null;
  billing_restricted_at: string | null;
}

interface OrgContextType {
  organizations: Org[];
  currentOrg: Org | null;
  currentOrgId: string | null;
  switchOrg: (orgId: string) => Promise<void>;
  isLoading: boolean;
  isReadOnly: boolean;
  isDemoRole: boolean;
  isBillingRestricted: boolean;
  // Kept for compatibility with pages that read these. Wire to real MFA later.
  mfaNeedsChallenge: boolean;
  mfaUserEnrolled: boolean;
  refreshMfa: () => Promise<void>;
}

const OrgContext = createContext<OrgContextType | null>(null);

export function OrgProvider({ children }: { children: ReactNode }) {
  const [organizations, setOrganizations] = useState<Org[]>([]);
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load orgs + active org on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [orgsRes, activeRes] = await Promise.all([
          fetch('/api/me/accessible-orgs', { credentials: 'include' }),
          fetch('/api/me/active-org', { credentials: 'include' }),
        ]);
        if (cancelled) return;

        if (orgsRes.ok) {
          const data = await orgsRes.json();
          const orgs: Org[] = (data.organizations || []).map((o: any) => ({
            id: o.id,
            name: o.name,
            slug: o.slug || '',
            role: o.role,
            is_demo: !!o.is_demo,
            mfa_required: !!o.mfa_required,
            server_count: o.server_count ?? 0,
            logo_url: o.logo_url ?? null,
            billing_restricted_at: o.billing_restricted_at ?? null,
          }));
          if (!cancelled) setOrganizations(orgs);
        }

        if (activeRes.ok) {
          const data = await activeRes.json();
          if (!cancelled) setCurrentOrgId(data.active_org_id ?? null);
        }
      } catch (err) {
        console.error('OrgContext: failed to load orgs', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const switchOrg = useCallback(async (orgId: string) => {
    const res = await fetch('/api/me/active-org', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ org_id: orgId }),
    });
    if (res.ok) {
      setCurrentOrgId(orgId);
      // Hard reload so every page-level query re-runs with the new org.
      window.location.reload();
    }
  }, []);

  const refreshMfa = useCallback(async () => {
    // No-op until real MFA lands. Kept so consumers don't crash.
  }, []);

  const currentOrg = organizations.find((o) => o.id === currentOrgId) ?? null;

  const value: OrgContextType = {
    organizations,
    currentOrg,
    currentOrgId,
    switchOrg,
    isLoading,
    isReadOnly: currentOrg?.role === 'stakeholder' || currentOrg?.role === 'demo',
    isDemoRole: currentOrg?.role === 'demo',
    isBillingRestricted: !!currentOrg?.billing_restricted_at,
    mfaNeedsChallenge: false,
    mfaUserEnrolled: false,
    refreshMfa,
  };

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg() {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error('useOrg must be used inside <OrgProvider>');
  return ctx;
}
